import { LoadingIndicator } from '../components/LoadingIndicator';
import { useQuery } from '@tanstack/react-query';
import type { TenantContext as Tenant } from '@delta/domain';
import { Banner, Button } from '../ui/semi';
import { createContext, type PropsWithChildren, useContext, useEffect, useState } from 'react';
import { publicApi } from '../lib/publicApi';
import { isUnknownTenantError, isTransientTenantContextError, shouldRetryTenantContext, tenantContextRecoveryInterval } from './tenantContextError';

const TenantContext = createContext<Tenant | null>(null);

export function TenantProvider({ children }: PropsWithChildren) {
  const [lastError, setLastError] = useState<unknown>(null);
  const tenantQuery = useQuery({
    queryKey: ['public', 'tenant-context', window.location.host],
    queryFn: async ({ signal }) => (await publicApi.get<Tenant>(
      '/public/tenant/context',
      { signal },
    )).data,
    staleTime: 5 * 60 * 1000,
    retry: shouldRetryTenantContext,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    refetchInterval: (query) => tenantContextRecoveryInterval(query.state),
    refetchOnWindowFocus: (query) => tenantContextRecoveryInterval(query.state) !== false,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (tenantQuery.isError) setLastError(tenantQuery.error);
    else if (tenantQuery.isSuccess) setLastError(null);
  }, [tenantQuery.isError, tenantQuery.isSuccess, tenantQuery.error]);

  useEffect(() => {
    if (tenantQuery.data?.name) {
      document.title = tenantQuery.data.name;
    }
  }, [tenantQuery.data?.name]);

  if (tenantQuery.isPending && !lastError) {
    return (
      <div className="full-page centered-page">
        <LoadingIndicator />
      </div>
    );
  }

  if (tenantQuery.isError || !tenantQuery.data) {
    const error = tenantQuery.error ?? lastError;
    const unknownTenant = isUnknownTenantError(error);
    const recovering = isTransientTenantContextError(error);
    return (
      <div className="full-page centered-page">
        <Banner
          className="tenant-context-status"
          type={recovering ? 'warning' : 'danger'}
          fullMode={false}
          closeIcon={null}
          description={(
            <div className="tenant-context-status-content" role="status" aria-live="polite">
              <h2>{unknownTenant ? '当前域名未绑定租户' : recovering ? '正在等待服务恢复' : '租户信息暂时无法加载'}</h2>
              <p>{unknownTenant
                ? '请检查域名解析、租户状态和反向代理的 Host 转发配置。'
                : recovering
                  ? '连接暂时中断，页面会自动重试，恢复后自动进入。'
                  : '请重试加载租户信息；如果仍然失败，请联系管理员。'}</p>
              <Button loading={tenantQuery.isFetching} onClick={() => { void tenantQuery.refetch(); }}>
                {tenantQuery.isFetching ? '正在重试' : '立即重试'}
              </Button>
            </div>
          )}
        />
      </div>
    );
  }

  return <TenantContext.Provider value={tenantQuery.data}>{children}</TenantContext.Provider>;
}

export function useTenant() {
  const tenant = useContext(TenantContext);
  if (!tenant) {
    throw new Error('useTenant 必须在 TenantProvider 内使用');
  }
  return tenant;
}
